import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, FileText, ExternalLink } from 'lucide-react';
import { useExposureFlag } from '@/hooks/useFeatureFlags';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useCustomerInvoicesForTicket, useCreateCustomerInvoice,
} from '@/hooks/invoicing';
import { formatInvoiceNumber } from '@/lib/invoicing/formatInvoiceNumber';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import { Link } from 'react-router-dom';

export const InvoicingCard = ({ ticketId }: { ticketId: string }) => {
  const enabled = useExposureFlag('ui_customer_invoices');
  const { canViewCustomerInvoices, canManageCustomerInvoices } = usePermissions();
  const { data: invoices, isLoading } = useCustomerInvoicesForTicket(ticketId, enabled && canViewCustomerInvoices);
  const createInvoice = useCreateCustomerInvoice();
  const [creating, setCreating] = useState(false);

  if (!enabled || !canViewCustomerInvoices) return null;

  const hasOpenDraft = (invoices ?? []).some((i) => i.status === 'estimate');

  const handleCreate = async () => {
    setCreating(true);
    try { await createInvoice.mutateAsync({ ticketId }); } finally { setCreating(false); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" /> Invoicing
        </CardTitle>
        {canManageCustomerInvoices && (
          <Button size="sm" onClick={handleCreate} disabled={creating || hasOpenDraft}>
            <Plus className="h-4 w-4 mr-1" />
            {hasOpenDraft ? 'Estimate open' : 'Create estimate'}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!isLoading && (invoices ?? []).length === 0 && (
          <div className="text-sm text-muted-foreground">No estimates or invoices yet.</div>
        )}
        {(invoices ?? []).map((inv) => (
          <div key={inv.id} className="flex items-center justify-between rounded-md border p-2">
            <div className="flex items-center gap-3 min-w-0">
              <InvoiceStatusBadge status={inv.status} />
              <div className="text-sm font-medium">{formatInvoiceNumber(inv)}</div>
              <div className="text-sm text-muted-foreground tabular-nums">
                ${Number(inv.grand_total).toFixed(2)}
                {Number(inv.balance_due) > 0 && (
                  <span className="ml-2 text-destructive">
                    Bal ${Number(inv.balance_due).toFixed(2)}
                  </span>
                )}
              </div>
            </div>
            <Button asChild size="sm" variant="ghost">
              <Link to={`/invoices/${inv.id}`}>
                <ExternalLink className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
